import { useEffect, useState } from "react";

import { AppBar, ErrorPage } from "@ezmid/components-library";
import type { AppBarItem } from "@ezmid/components-library";
import {
  HomeIcon as HomeIconOutline,
  UsersIcon as UsersIconOutline,
  Square3Stack3DIcon as Square3Stack3DIconOutline,
  AcademicCapIcon as AcademicCapIconOutline,
  WrenchScrewdriverIcon as WrenchScrewdriverIconOutline,
  PowerIcon as PowerIconOutline,
  CreditCardIcon as CreditCardIconOutline,
  ReceiptRefundIcon as ReceiptRefundIconOutline,
  ChevronRightIcon,
  ComputerDesktopIcon as ComputerDesktopIconOutline,
} from "@heroicons/react/24/outline";
import {
  ComputerDesktopIcon,
  PowerIcon,
  AcademicCapIcon,
  HomeIcon,
  Square3Stack3DIcon,
  ChevronDoubleRightIcon,
  CreditCardIcon,
  ReceiptRefundIcon,
  WrenchScrewdriverIcon,
  UsersIcon,
} from "@heroicons/react/24/solid";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";

import { makeRoute } from "../../hooks/route";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import { authSelector, logoutUser } from "../auth/authSlice";
import { PageLoader } from "../loader/PageLoader";
import ApplicationRoute from "../route/ApplicationRoute";
import { AllRoutes } from "../route/Route";

// TODO: Someday in the future implement something meaningful here
const myErrorHandler = (error: Error, info: { componentStack: string }) => {
  // Do something with the error
  // E.g. log to an error logging client here
};
type RoutesId =
  | "dashboard"
  | "users"
  | "payments"
  | "subscriptions"
  | "products"
  | "lecturers"
  | "settings"
  | "logout"
  | "courses";

const mapRouteIdToApplicationRoute: {
  [key in RoutesId]: ApplicationRoute;
} = {
  dashboard: AllRoutes.DASHBOARD,
  users: AllRoutes.USERS_LIST,
  payments: AllRoutes.PAYMENTS_LIST,
  subscriptions: AllRoutes.SUBSCRIPTIONS_LIST,
  products: AllRoutes.PRODUCTS_LIST,
  lecturers: AllRoutes.LECTURERS_LIST,
  settings: AllRoutes.USER_SETTINGS,
  logout: AllRoutes.AUTH_LOGOUT,
  courses: AllRoutes.COURSES_LIST,
};

export function PrivateContent() {
  const { isInitialized, isLoggedIn, error } = useAppSelector(authSelector);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const items: Array<AppBarItem> = [
    {
      title: "/bigsysther-appbar-logo.svg",
      type: "logo",
    },
    {
      type: "divider",
    },
    {
      title: t("app.page.dashboard.heading"),
      icon: <HomeIconOutline />,
      iconSelected: <HomeIcon />,
      color: "white",
      isActive: isLoggedIn,
      type: "item",
      routeId: "dashboard",
    },
    {
      title: t("app.page.users.heading"),
      icon: <UsersIconOutline />,
      iconSelected: <UsersIcon />,
      color: "white",
      isActive: false,
      type: "item",
      routeId: "users",
    },
    {
      title: t("app.page.courses.heading"),
      icon: <ComputerDesktopIconOutline />,
      iconSelected: <ComputerDesktopIcon />,
      color: "white",
      isActive: false,
      type: "item",
      routeId: "courses",
    },

    {
      title: t("app.page.products.heading"),
      icon: <Square3Stack3DIconOutline />,
      iconSelected: <Square3Stack3DIcon />,
      color: "white",
      isActive: false,
      type: "item",
      routeId: "products",
    },
    {
      title: t("app.page.lecturers.heading"),
      icon: <AcademicCapIconOutline />,
      iconSelected: <AcademicCapIcon />,
      color: "white",
      isActive: false,
      type: "item",
      routeId: "lecturers",
    },
    {
      title: t("app.page.payments.heading"),
      icon: <CreditCardIconOutline />,
      iconSelected: <CreditCardIcon />,
      color: "white",
      isActive: false,
      type: "item",
      routeId: "payments",
    },
    {
      title: t("app.page.subscriptions.heading"),
      icon: <ReceiptRefundIconOutline />,
      iconSelected: <ReceiptRefundIcon />,
      color: "white",
      isActive: false,
      type: "item",
      routeId: "subscriptions",
    },
    {
      title: t("app.settings"),
      icon: <WrenchScrewdriverIconOutline />,
      iconSelected: <WrenchScrewdriverIcon />,
      isActive: false,
      color: "white",
      type: "item",
      routeId: "settings",
    },
  ];

  const itemsBottom: Array<AppBarItem> = [
    {
      icon: <ChevronDoubleRightIcon />,
      color: "white",
      isActive: false,
      type: "widthControl",
    },

    {
      title: t("app.auth.logout"),
      icon: <PowerIconOutline />,
      iconSelected: <PowerIcon />,
      isActive: false,
      color: "white",
      type: "item",
      routeId: "logout",
    },
  ];

  const routeName = location.pathname.split("/")[2];
  const [menutItems, setMenuItems] = useState<Array<AppBarItem>>(items);

  useEffect(
    function selectMenuItemOnRouteChange() {
      const activeMenuItems = items.map((item) => ({
        ...item,
        isActive: item.routeId === routeName,
      }));
      setMenuItems(activeMenuItems);
    },
    [routeName]
  );

  const logout = async () => {
    dispatch(logoutUser());
  };

  const handleClick = (routeId: RoutesId) => {
    if (routeId === "logout") {
      return logout();
    }
    const route = mapRouteIdToApplicationRoute[routeId];

    return navigate(makeRoute(route));
  };
  if (!isInitialized) {
    return <PageLoader />;
  }

  if (!isLoggedIn) {
    return <Navigate to={AllRoutes.AUTH_LOGIN.path} />;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorPage} onError={myErrorHandler}>
      {!isInitialized && error ? (
        <ErrorPage
          errorMessageText={t("app.page.error.defaultMessage")}
          messageForUser={t("app.page.error.ups")}
          hasGoBackButton
          buttonText={t("app.goToDasboard")}
          onButtonClick={() => navigate(makeRoute(AllRoutes.DASHBOARD))}
        />
      ) : (
        <div className="flex">
          <AppBar
            items={menutItems}
            onMenuItemClick={handleClick}
            subItemsIcon={<ChevronRightIcon />}
            itemsBottom={itemsBottom}
          />
          <Outlet />
        </div>
      )}
    </ErrorBoundary>
  );
}
